/**
 * tpl-mat-scroll3d.js: the framed Architectural Mat, built in a studio as
 * you scroll. This is the core: the real geometry, the choreography, the
 * spec callouts and the scroll painter. It is the only file the section
 * loads up front. The rest arrive on demand:
 *
 *   tpl-mat-scroll3d-scene.js  three.js studio scene (desktop, bake, film)
 *   tpl-mat-scroll3d-flat.js   phone compositor over the baked frames
 *   tpl-mat-scroll3d-data.js   per-frame geometry of the bake
 *
 * The object never floats: it stands on the studio floor and the camera
 * moves around it the way it would on a shoot. Parts slide in along the
 * floor into an exploded axonometric, then seat.
 *
 * Kept from the first version, because each one cost real time: a
 * TPLScrollBus painter (no scroll listener of its own), a render loop that
 * idles, three.js injected as a module script (Shopify rewrites import() in
 * a .js asset), visibility from the rect, and errors written to
 * data-s3d-error instead of thrown inside the rAF.
 *
 * QA: ?s3d=frames forces the phone path, ?s3d=webgl the live scene,
 * ?s3d=bake exposes the bake and the studio renders.
 */
(function () {
  'use strict';

  var NS = (window.TPLMatS3D = window.TPLMatS3D || {});
  if (NS.core) return;
  NS.core = true;

  var QS = location.search;
  var BAKING = /[?&]s3d=bake/.test(QS);

  /* ---- the build, in millimetres, at 10 inch ----
     16 inch is the same object scaled; insets are percentages so the two
     read the same. Object space: floor at y = 0, panel face at z = 0,
     x centred. */
  var C = (NS.C = {
    P: 254,
    T: 3,
    RADIUS: 2.2,
    INSET: 0.15,
    BLEED: 5,
    SPACER_W: 14,
    SPACER_D: 3,
    BOARD_D: 2.5,
    /* moulding: face width, lip over the panel, lip thickness, depth */
    FACE: 9,
    LIP: 4,
    LIP_T: 5,
    DEPTH: 22,
    CLEAR: 0.5,
    BAKE_PX: 720
  });
  C.HO = C.P / 2 - C.LIP + C.FACE;
  C.LIFT = C.FACE - C.LIP;

  var SHAPES = { v: [0.375, 0.5], h: [0.5, 0.375], sq: [0.4, 0.4] };
  var MAT_NAMES = (NS.MAT_NAMES = ['Silver', 'Marine', 'Burgundy']);
  var MAT_FALLBACK = { Silver: '#9da1a6', Marine: '#163367', Burgundy: '#60373e' };
  var FINISH_NAMES = (NS.FINISH_NAMES = ['Gallery White', 'Ebony Black', 'Walnut Brown']);

  /* ---- small helpers ---- */
  var U = (NS.util = {});
  U.clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  U.lerp = function (a, b, t) { return a + (b - a) * t; };
  U.seg = function (p, a, b) { return U.clamp((p - a) / (b - a), 0, 1); };
  /* cubic in-out: every move starts and lands softly, nothing is linear */
  U.ease = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };
  U.num = function (el, name, fallback) {
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? fallback : v;
  };
  U.hexRgb = function (hex) {
    var h = String(hex || '').replace('#', '').trim();
    if (h.length === 3) h = h.charAt(0) + h.charAt(0) + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2);
    if (!/^[0-9a-f]{6}$/i.test(h)) return null;
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };
  U.rgbHex = function (c) {
    return '#' + c.map(function (v) { return ('0' + Math.round(U.clamp(v, 0, 255)).toString(16)).slice(-2); }).join('');
  };
  U.json = function (el, name, fallback) {
    try { return JSON.parse(el.getAttribute(name) || ''); } catch (err) { return fallback; }
  };
  var clamp = U.clamp, lerp = U.lerp, seg = U.seg, ease = U.ease;

  /* ---- the window this design has ----
     Shape follows the photograph (the master's aspect forces it); position
     comes from the setting or a metafield; 'custom' keeps millimetres. */
  NS.windowRect = function (stage) {
    var P = C.P;
    var shape = String(stage.getAttribute('data-s3d-shape') || 'auto').toLowerCase();
    var pos = String(stage.getAttribute('data-s3d-pos') || 'c').toLowerCase();
    if (shape === 'auto') {
      var ar = U.num(stage, 'data-s3d-art-ratio', 0);
      shape = !ar || ar < 0.9 ? 'v' : ar > 1.12 ? 'h' : 'sq';
    }
    var r;
    if (SHAPES[shape]) {
      var w = SHAPES[shape][0] * P, h = SHAPES[shape][1] * P, m = C.INSET * P;
      var vert = pos.length === 2 ? pos.charAt(0) : 'c';
      var horz = pos.length === 2 ? pos.charAt(1) : 'c';
      r = {
        w: w, h: h,
        x: horz === 'l' ? m : horz === 'r' ? P - m - w : (P - w) / 2,
        y: vert === 't' ? P - m - h : vert === 'b' ? m : (P - h) / 2
      };
    } else {
      r = {
        w: U.num(stage, 'data-s3d-window-w', 95.25), h: U.num(stage, 'data-s3d-window-h', 127),
        x: U.num(stage, 'data-s3d-inset-x', 38.1), y: U.num(stage, 'data-s3d-inset-y', 88.9)
      };
    }
    r.w = clamp(r.w, 20, P - 20);
    r.h = clamp(r.h, 20, P - 20);
    r.x = clamp(r.x, 8, P - 8 - r.w);
    r.y = clamp(r.y, 8, P - 8 - r.h);
    r.r = Math.min(C.RADIUS, r.w / 4, r.h / 4);
    r.fw = Math.round((r.w / P) * 1000) / 10;
    r.fh = Math.round((r.h / P) * 1000) / 10;
    return r;
  };

  function pct(v) { return (Math.round(v * 2) / 2) + '%'; }
  /* Inches at both sizes, from the window's share of the panel (owner 2026-10-27:
     no millimetres on the page). 40% -> "4 or 6.4 in"; 37.5% -> "3.75 or 6 in". */
  function inch(v) { return String(Math.round(v * 100) / 100); }
  function inches(sharePct) { return inch(sharePct / 10) + ' or ' + inch(sharePct * 0.16) + ' in'; }
  NS.inches = inches;

  NS.windowSpec = function (win) {
    if (Math.abs(win.fw - win.fh) < 0.6) return 'Cut to ' + inches(win.fw) + ', each way';
    return 'Cut to ' + inches(win.fw) + ' wide and ' + inches(win.fh) + ' tall';
  };

  /* ---- the look: mat colour and frame finish ---- */
  NS.readTints = function (stage) {
    var map = U.json(stage, 'data-s3d-tints', {}) || {};
    var out = {};
    MAT_NAMES.forEach(function (n) {
      out[n] = (map[n] && U.hexRgb(map[n].face) && map[n].face) || MAT_FALLBACK[n];
    });
    return out;
  };

  function optionValue(names) {
    for (var i = 0; i < names.length; i++) {
      var sel = document.querySelector('select[name="options[' + names[i] + ']"]');
      if (sel && sel.value) return sel.value;
      var radio = document.querySelector('input[type="radio"][name^="' + names[i] + '-"]:checked');
      if (radio && radio.value) return radio.value;
    }
    return null;
  }

  NS.lookOf = function (stage, tints, hint) {
    var follow = stage.getAttribute('data-s3d-follow') === 'true';
    /* a choice made in the section itself, where there is no buy box */
    hint = Object.assign({}, stage.tplHint || {}, hint || {});
    var mat = hint.mat || (follow && optionValue(['Mat'])) || stage.getAttribute('data-s3d-mat');
    var finish = hint.finish || (follow && optionValue(['Framing', 'Frame'])) || stage.getAttribute('data-s3d-finish');
    var md = stage.getAttribute('data-s3d-mat-default');
    var fd = stage.getAttribute('data-s3d-finish-default');
    if (!tints[mat]) mat = tints[md] ? md : 'Marine';
    if (FINISH_NAMES.indexOf(finish) < 0) finish = FINISH_NAMES.indexOf(fd) >= 0 ? fd : 'Ebony Black';
    return { mat: mat, matHex: tints[mat], finish: finish };
  };

  /* Mat tiles and the Framing menu both mirror into selects and fire a
     bubbling change; a tile click carries its own value in case the select
     has not caught up. */
  NS.watchLook = function (stage, onChange) {
    if (stage.getAttribute('data-s3d-follow') !== 'true') return function () {};
    function handler(event) {
      var t = event.target;
      if (!t || !t.closest) return;
      var hint = {};
      if (event.type === 'click') {
        var mt = t.closest('.tpl-mat-swatch[data-mat-value]');
        var ft = t.closest('.tpl-frame-swatch[data-frame-value]');
        if (!mt && !ft) return;
        if (mt) hint.mat = mt.getAttribute('data-mat-value');
        if (ft) hint.finish = ft.getAttribute('data-frame-value');
      } else if (!t.closest('variant-selects, product-form, form, [data-mat-select], .tpl-mat-picker, .tpl-frame-select')) {
        return;
      }
      requestAnimationFrame(function () { onChange(hint); });
    }
    document.addEventListener('change', handler);
    document.addEventListener('click', handler);
    return function () {
      document.removeEventListener('change', handler);
      document.removeEventListener('click', handler);
    };
  };

  /* Collection-header mode walks the mats across the scroll. */
  NS.cycleAt = function (tints, p) {
    var n = MAT_NAMES.length;
    var t = clamp(p, 0, 0.9999) * n;
    var i = Math.floor(t);
    var f = ease(clamp((t - i - 0.6) / 0.4, 0, 1));
    var j = Math.min(i + 1, n - 1);
    var a = U.hexRgb(tints[MAT_NAMES[i]]), b = U.hexRgb(tints[MAT_NAMES[j]]);
    return { name: f < 0.5 ? MAT_NAMES[i] : MAT_NAMES[j], hex: U.rgbHex([lerp(a[0], b[0], f), lerp(a[1], b[1], f), lerp(a[2], b[2], f)]) };
  };

  /* ---- the choreography: pure functions of progress ----
     Camera keys: progress, azimuth and elevation (degrees), the span in mm
     the frame must hold, and the point it looks at. Each leg eases in and
     out, so the camera settles at every stage the way a dolly does. */
  var CAM = [
    /* p      az    el    span  target x, y, z. The camera always looks at
       the middle of the whole object and always holds all of it, so the piece
       sits in the true centre of the stage at every stage (owner 10-27). */
    [0.00, -36, 8, 470, 0, 132, -2],
    [0.13, 0, 3, 430, 0, 132, -2],
    [0.21, 14, 4, 420, 0, 132, -2],
    [0.29, -8, 4, 420, 0, 132, -2],
    [0.40, 40, 20, 700, 0, 150, -20],
    [0.47, 44, 22, 700, 0, 150, -20],
    [0.58, 24, 10, 470, 0, 135, -6],
    [0.68, 30, 14, 440, 0, 135, -6],
    [0.78, 36, 12, 430, 0, 135, -6],
    /* the last stage makes room for the paperback on the right */
    [0.90, -5, 3, 520, 75, 140, -6],
    [1.00, -3, 2, 510, 75, 140, -6]
  ];

  function camKey(k, win, aspect) {
    var span = k[3] === 'c' ? Math.min(210, 240 / Math.max(1, aspect || 1)) : k[3] || Math.max(win.w, win.h) * 3.2;
    var tx = k[4], ty = k[5];
    if (tx === 'w') { tx = win.x + win.w / 2 - C.P / 2; ty = win.y + win.h / 2 + 6; }
    /* a corner close-up: the corner sits right of centre, open studio beyond it */
    if (tx === 'c') { tx = C.HO - 50; ty = 2 * C.HO - 45; }
    return { az: k[1], el: k[2], span: span, tx: tx, ty: ty, tz: k[6] };
  }

  /* The middle of the stack's depth, from the parts that are there (a
     part still fading in counts for less), so the whole object stays
     centred while the layers spread out and close up again. */
  function depthCentre(p) {
    /* the camera makes room for each part a moment before it appears */
    var parts = NS.partsAt(p), soon = NS.partsAt(Math.min(1, p + 0.05)), T = C.T, B = C.BOARD_D;
    ['spacer', 'board', 'frame'].forEach(function (k) {
      parts[k] = { x: parts[k].x, z: parts[k].z, a: Math.max(parts[k].a, soon[k].a), on: parts[k].on || soon[k].on };
    });
    var hi = -Infinity, lo = Infinity;
    [[parts.panel, 0, -T], [parts.board, -T, -T - B], [parts.spacer, -T - B, -T - B - C.SPACER_D],
      [parts.frame, C.LIP_T, C.LIP_T - C.DEPTH]].forEach(function (r) {
      var part = r[0];
      if (!part.on) return;
      var w = part.a;
      hi = Math.max(hi, lerp(0, part.z + r[1], w));
      lo = Math.min(lo, lerp(-T, part.z + r[2], w));
    });
    return (hi + lo) / 2;
  }

  NS.cameraAt = function (p, win, aspect, centred) {
    var a = CAM[0], b = CAM[0];
    for (var i = 1; i < CAM.length; i++) {
      if (p <= CAM[i][0]) { a = CAM[i - 1]; b = CAM[i]; break; }
      a = b = CAM[i];
    }
    var t = a === b ? 0 : ease((p - a[0]) / (b[0] - a[0]));
    var A = camKey(a, win, aspect), B = camKey(b, win, aspect);
    /* nothing closes the scroll: the finished piece comes back to the true centre */
    if (centred) {
      if (a[0] >= 0.9) { A.tx = 0; A.span = 470; }
      if (b[0] >= 0.9) { B.tx = 0; B.span = 470; }
    }
    /* on a phone (and in its bake) the finished piece rises in the frame,
       leaving the lower part of the stage to the label and the choices */
    var raise = aspect && aspect < 1.3 ? 70 * ease(seg(p, 0.84, 0.92)) : 0;
    return {
      az: lerp(A.az, B.az, t), el: lerp(A.el, B.el, t), span: lerp(A.span, B.span, t),
      tx: lerp(A.tx, B.tx, t), ty: lerp(A.ty, B.ty, t) - raise, tz: depthCentre(p)
    };
  };

  /* Where each part stands. Back to front: the stretcher, the photograph on
     its white board, the panel, the frame. Each fades in at its own place in
     the stack, drifting the last few centimetres in depth, so nothing ever
     enters from beyond the edge of a narrow screen (owner 10-27); then it all
     closes up under the frame. `a` is how present a part is; the phone bake
     takes it as on or off. */
  NS.partsAt = function (p) {
    function e(a, b) { return ease(seg(p, a, b)); }
    function part(x, z, lift, a) {
      if (BAKING && !NS.studioRendering) a = a >= 0.5 ? 1 : 0;
      return { x: x, z: z, lift: lift, a: a, on: a > 0.01 };
    }
    var lift = C.LIFT * e(0.55, 0.59);
    return {
      spacer: part(0, (-185 - 60 * (1 - e(0.31, 0.41))) * (1 - e(0.47, 0.57)), lift, e(0.305, 0.35)),
      board: part(0, (-95 - 45 * (1 - e(0.33, 0.43))) * (1 - e(0.46, 0.55)), lift, e(0.325, 0.37)),
      panel: part(0, 0, lift, 1),
      frame: part(0, (150 + 60 * (1 - e(0.35, 0.45))) * (1 - e(0.49, 0.59)), 0, e(0.345, 0.39)),
      /* the rebate shadow on the face arrives as the frame seats */
      seated: e(0.56, 0.6)
    };
  };

  /* ---- the spec callouts ----
     Plain and factual: no counts, no em-dashes, no tags. {window} is this
     design's cut, {finish} the frame on screen. */
  var CHAPTERS = (NS.CHAPTERS = [
    { at: 0.00, label: 'The panel', specs: ['3 mm aluminium composite', 'Silver, Marine or Burgundy'] },
    { at: 0.16, label: 'The window', specs: ['A window cut to the photograph', '{window}', 'Rounded corners, black core edge'] },
    { at: 0.32, label: 'The build', specs: ['Frame, panel, photograph on its board, stretcher', 'All of it closes up under the frame'] },
    { at: 0.48, label: 'The photograph', specs: ['Printed on Hahnemühle Fine Art Matte 210gsm', 'Mounted on a white backing board', 'Held from behind by the stretcher'] },
    { at: 0.61, label: 'The frame', specs: ['Gallery White, Ebony Black or Walnut Brown', 'Mitred corners, rebated moulding', 'Shown in {finish}'] },
    { at: 0.84, label: 'The finished piece', specs: ['10 or 16 inch, square', 'Made to order'] }
  ]);

  /* Where the scroll ends (owner 2026-10-30): on the angled "The frame"
     stage, the camera's 0.78 key, where the leg eases to a stop. The whole
     scroll drives the choreography from 0 to there, so every stage keeps
     its order and spacing; "finished" runs on to the face-on piece as
     before. Both paths read progress through NS.progressOf. */
  var END_AT = (NS.END_AT = { frame: 0.78, finished: 1 });
  NS.endP = function (stage) {
    if (BAKING) return 1;
    if (stage.tplEndP == null) stage.tplEndP = END_AT[stage.getAttribute('data-s3d-end') || 'frame'] || END_AT.frame;
    return stage.tplEndP;
  };

  NS.chapterAt = function (p) {
    var i = 0;
    for (var n = 0; n < CHAPTERS.length; n++) { if (p >= CHAPTERS[n].at) i = n; }
    return i;
  };

  /* Drawing annotations, each tied to a plane of the object so both render
     paths can place them: 'panel' and 'panelBack' (mm from the panel's
     bottom-left), 'spacer', 'board', and 'frame' (mm from the frame's outer
     bottom-left). p0..p1 is when each one is up. */
  NS.annotations = function (win, parts) {
    parts = parts || NS.PARTS;
    var P = C.P, HO2 = 2 * C.HO;
    var wTop = win.y + win.h, wl = win.x;
    return [
      { kind: 'dim', plane: 'panel', a: [0, P + 20], b: [P, P + 20], ext: [P + 4, P + 26], label: '10 or 16 in', p0: 0.075, p1: 0.15 },
      { kind: 'note', plane: 'panel', a: [0, P * 0.72], label: '\u215B in', dx: -64, dy: -26, p0: 0.02, p1: 0.08 },
      { kind: 'dim', plane: 'panel', a: [wl, wTop - 14], b: [wl + win.w, wTop - 14], label: inches(win.fw), p0: 0.2, p1: 0.31 },
      { kind: 'dim', plane: 'panel', a: [wl + 14, win.y], b: [wl + 14, wTop], vertical: true, side: 1, label: inches(win.fh), p0: 0.2, p1: 0.31 },
      /* exploded view: the front two labelled on the left, the back two on
         the right, each pointing at an edge that faces its own side */
      { kind: 'part', side: -1, plane: 'frame', a: [C.FACE * 0.5, HO2 * 0.3], label: parts[0][0], sub: parts[0][1], p0: 0.44, p1: 0.48 },
      { kind: 'part', side: -1, plane: 'panel', a: [P * 0.05, P * 0.985], label: parts[1][0], sub: parts[1][1], p0: 0.42, p1: 0.48 },
      { kind: 'part', side: 1, plane: 'board', a: [P * 0.95, P * 0.03], label: parts[2][0], sub: parts[2][1], p0: 0.43, p1: 0.48 },
      { kind: 'part', side: 1, plane: 'spacer', a: [P - C.SPACER_W / 2, P * 0.7], label: parts[3][0], sub: parts[3][1], p0: 0.41, p1: 0.48 }
    ];
  };

  NS.fill = function (text, look, win) {
    return text
      .replace(/\{finish\}|%finish%/, look ? look.finish : 'Ebony Black')
      .replace(/\{mat\}|%mat%/, look ? look.mat : 'Marine')
      .replace(/\{window\}|%window%/, win ? NS.windowSpec(win) : '');
  };

  /* the wall throws a soft shadow onto the photograph a few mm behind it */
  /* The shadow the window's cut wall throws onto the photograph, which sits
     a few millimetres behind the colour. `L` is the light's direction in the
     panel's space (x right, y up, z out); the wall on the light's side shades
     a band whose width is the depth times the light's slant. Without L it is
     the approved top-left look. Draws into `into` when given. */
  NS.insetShadow = function (win, L, into) {
    var long = Math.max(win.w, win.h);
    var c = into || document.createElement('canvas');
    var cw = Math.round(256 * win.w / long), ch = Math.round(256 * win.h / long);
    if (c.width !== cw) c.width = cw;
    if (c.height !== ch) c.height = ch;
    var g = c.getContext('2d'), k = c.width / win.w;
    g.clearRect(0, 0, c.width, c.height);
    function band(x0, y0, x1, y1, a) {
      var gr = g.createLinearGradient(x0, y0, x1, y1);
      gr.addColorStop(0, 'rgba(0,0,0,' + a.toFixed(3) + ')'); gr.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = gr;
      g.fillRect(Math.min(x0, x1), Math.min(y0, y1), Math.abs(x1 - x0) || c.width, Math.abs(y1 - y0) || c.height);
    }
    var w = { t: 6, l: 3.5, r: 2, b: 2 };
    if (L) {
      var z = Math.max(0.25, L[2]), D = 3.6;
      w = { t: 1.4 + D * Math.max(0, L[1]) / z, b: 1.4 + D * Math.max(0, -L[1]) / z, l: 1.4 + D * Math.max(0, -L[0]) / z, r: 1.4 + D * Math.max(0, L[0]) / z };
    }
    function a(width) { return 0.14 + 0.32 * clamp(width / 6, 0, 1); }
    band(0, 0, 0, w.t * k, a(w.t));
    band(0, 0, w.l * k, 0, a(w.l));
    band(c.width, 0, c.width - w.r * k, 0, a(w.r));
    band(0, c.height, 0, c.height - w.b * k, a(w.b));
    return c;
  };

  /* fine matte tooth for the photograph, multiplied in once */
  NS.paperTooth = function (img, size) {
    var c = document.createElement('canvas');
    c.width = img.naturalWidth || img.width; c.height = img.naturalHeight || img.height;
    var g = c.getContext('2d');
    g.drawImage(img, 0, 0);
    var t = document.createElement('canvas'); t.width = t.height = 64;
    var tg = t.getContext('2d'), d = tg.createImageData(64, 64);
    for (var i = 0; i < d.data.length; i += 4) { var v = 236 + Math.random() * 19; d.data[i] = d.data[i + 1] = d.data[i + 2] = v; d.data[i + 3] = 255; }
    tg.putImageData(d, 0, 0);
    g.globalCompositeOperation = 'multiply';
    g.globalAlpha = 0.5;
    var step = Math.max(64, Math.round((size || 1) * 64));
    for (var y = 0; y < c.height; y += step) for (var x = 0; x < c.width; x += step) g.drawImage(t, x, y, step, step);
    g.globalCompositeOperation = 'source-over'; g.globalAlpha = 1;
    return c;
  };

  /* Is the page dark? The theme's toggle sets data-theme; fall back to the
     body's own background in case a scheme darkens it another way. */
  NS.isDark = function () {
    if (document.documentElement.getAttribute('data-theme') === 'dark') return true;
    var m = (getComputedStyle(document.body).backgroundColor || '').match(/\d+(\.\d+)?/g);
    if (!m || m.length < 3 || (m.length > 3 && +m[3] === 0)) return false;
    return (0.2126 * m[0] + 0.7152 * m[1] + 0.0722 * m[2]) / 255 < 0.3;
  };

  /* ---- the DOM ---- */
  var WIDE = window.matchMedia('(min-width: 990px)');
  /* The exploded-view labels: name and one short line, front to back. */
  NS.PARTS = [['Frame', 'Mitred, rebated moulding'], ['Panel', '3 mm aluminium composite'], ['Photograph', 'On its backing board'], ['Stretcher', '']];

  /* Copy comes from the section settings (data-s3d-copy), so it can be
     rewritten without touching this file. Each stage is a label and a block
     of lines, one per line; {window} and {finish} still fill in. Anything
     missing falls back to the text above. */
  function lines(v) {
    return String(v == null ? '' : v).split(/\r?\n|\\n/).map(function (x) { return x.trim(); }).filter(Boolean);
  }
  NS.copyOf = function (stage) {
    var c = U.json(stage, 'data-s3d-copy', null) || {};
    var stages = c.stages || [];
    var chapters = CHAPTERS.map(function (ch, i) {
      var s = stages[i] || {};
      var l = lines(s.lines);
      return { at: ch.at, label: (s.label && String(s.label).trim()) || ch.label, specs: l.length ? l : ch.specs };
    });
    var parts = NS.PARTS.map(function (d, i) {
      var row = lines(c.parts)[i];
      if (!row) return d;
      var bits = row.split('|');
      return [bits[0].trim() || d[0], (bits[1] || '').trim()];
    });
    return { chapters: chapters, parts: parts };
  };

  NS.els = function (stage) {
    var copy = NS.copyOf(stage);
    return {
      chapters: copy.chapters,
      parts: copy.parts,
      stage: stage,
      sticky: stage.querySelector('[data-s3d-sticky]'),
      rail: stage.querySelector('[data-s3d-rail]'),
      stand: stage.querySelector('[data-s3d-stand]'),
      top: stage.querySelector('[data-s3d-top]'),
      label: stage.querySelector('[data-s3d-label]'),
      specs: stage.querySelector('[data-s3d-specs]'),
      cta: stage.querySelector('[data-s3d-cta]'),
      ov: stage.querySelector('[data-s3d-ov]'),
      end: stage.querySelector('[data-s3d-end]'),
      book: stage.querySelector('[data-s3d-book]'),
      closeNone: stage.getAttribute('data-s3d-close') === 'none',
      quietEnd: stage.hasAttribute('data-s3d-quiet-end'),
      spec: stage.querySelector('[data-s3d-m-spec]'),
      sizeNote: stage.querySelector('[data-s3d-size-note]'),
      buy: stage.querySelector('[data-s3d-buy]'),
      items: null,
      box: null
    };
  };

  function riseWords(node) {
    var spans = window.TPLScrollFx && window.TPLScrollFx.splitWords ? window.TPLScrollFx.splitWords(node) : null;
    if (!spans || !spans.length) {
      node.classList.remove('is-in');
      void node.offsetWidth;
      node.classList.add('is-in');
      return;
    }
    spans.forEach(function (sp, i) {
      sp.style.animation = 'none';
      void sp.offsetWidth;
      sp.style.animation = 'tpl-m3d-word .6s cubic-bezier(.16,1,.3,1) ' + i * 0.05 + 's both';
    });
  }

  /* On a phone or tablet the caption changes with one quiet fade: no
     word-by-word rise, no staggered rows (owner 2026-10-30: calm). */
  var CALM = 'tpl-m3d-fade .45s ease both';
  function calmIn(node) {
    node.style.animation = 'none';
    void node.offsetWidth;
    node.style.animation = CALM;
  }

  /* One place paints the copy for a given progress, used by both paths. */
  NS.paintChapter = function (el, p, look, win) {
    NS.paintBuy(el, p);
    if (el.rail) el.rail.style.transform = 'scaleX(' + clamp(p, 0, 1).toFixed(3) + ')';
    if (el.stand) el.stand.style.opacity = ease(seg(p, 0.0, 0.03)).toFixed(3);
    /* the headline introduces the piece, then clears the stage for the
       drawing, and comes back for the finished piece */
    if (el.top) {
      /* with the last-stage label in place, the headline only opens */
      var o = Math.max(1 - ease(seg(p, 0.05, 0.085)), el.end || el.closeNone ? 0 : ease(seg(p, 0.9, 0.96)));
      el.top.style.opacity = o.toFixed(3);
      /* 1 Nov: on phones the stage heading takes the title's place as the title goes (CSS reads this) */
      if (el.stage) el.stage.style.setProperty('--m3d-title-o', o.toFixed(3));
    }

    if (el.cta && !el.ctaOff) {
      var show = p > 0.9;
      if (show === el.cta.hidden) {
        el.cta.hidden = !show;
        if (show) { el.cta.classList.remove('is-in'); void el.cta.offsetWidth; el.cta.classList.add('is-in'); }
        /* on a phone the close takes the copy's place */
        el.stage.classList.toggle('is-cta', show);
      }
    }

    if (!el.label) return -1;
    var i = NS.chapterAt(p);
    /* While the exploded labels are up: on desktop the copy steps aside so
       nothing competes with them; on a narrow screen there are no floating
       labels, and the copy under the object lists the parts instead. */
    var wide = WIDE.matches, partsUp = p > 0.405 && p < 0.485;
    var list = partsUp && !wide && el.parts && el.parts.length ? el.parts : null;
    /* with no closing, the last chapter is the piece alone: the words step away */
    var quiet = (el.closeNone || el.quietEnd) && i === CHAPTERS.length - 1;
    if (el.label.parentNode) el.label.parentNode.style.opacity = (wide && partsUp) || quiet ? '0' : '';
    var key = i + '|' + (look ? look.finish : '') + (list ? '|parts' : '');
    if (el.label.getAttribute('data-ch') === key) return i;
    var same = el.label.getAttribute('data-ch-i') === String(i);
    var turned = !same || !!list !== !!el.listed;
    el.listed = !!list;
    el.label.setAttribute('data-ch', key);
    el.label.setAttribute('data-ch-i', String(i));
    var c = (el.chapters || CHAPTERS)[i];
    if (!same) {
      el.label.textContent = c.label;
      if (wide) riseWords(el.label); else calmIn(el.label);
    }
    if (el.specs) {
      el.specs.textContent = '';
      (list || c.specs).forEach(function (row, n) {
        var li = document.createElement('li');
        if (list) {
          var name = document.createElement('span');
          name.className = 'tpl-m3d__pn';
          name.textContent = row[0];
          li.appendChild(name);
          if (row[1]) li.appendChild(document.createTextNode(row[1]));
        } else {
          li.textContent = NS.fill(row, look, win);
        }
        if (turned) li.style.animation = wide ? 'tpl-m3d-row .6s cubic-bezier(.16,1,.3,1) ' + (0.1 + n * 0.07) + 's both' : CALM;
        el.specs.appendChild(li);
      });
    }
    return i;
  };

  /* ---- the drawing overlay: dimension lines, notes and part labels ----
     `project(plane, u, v)` returns canvas pixels (or null); `box` maps them
     into the sticky. Everything is created once and only moved after. */
  var SVGNS = 'http://www.w3.org/2000/svg';
  function svg(tag, cls, parent) {
    var n = document.createElementNS(SVGNS, tag);
    if (cls) n.setAttribute('class', cls);
    parent.appendChild(n);
    return n;
  }

  function buildOverlay(el, notes) {
    var ov = el.ov;
    ov.textContent = '';
    el.items = notes.map(function (n) {
      var g = svg('g', 'tpl-m3d__ov-item tpl-m3d__ov-' + n.kind, ov);
      var it = { n: n, g: g, line: svg('path', 'tpl-m3d__ov-line', g), on: -1 };
      if (n.kind === 'dim') it.ext = svg('path', 'tpl-m3d__ov-ext', g);
      if (n.kind === 'note' || n.kind === 'part') {
        /* a ring in the page colour with a thin ink outline under each dot,
           so the dot reads on any finish, on a light or a dark page */
        it.ring = svg('circle', 'tpl-m3d__ov-ring', g);
        it.dot = svg('circle', 'tpl-m3d__ov-dot', g);
      }
      it.text = svg('text', 'tpl-m3d__ov-label', g);
      it.text.textContent = n.label;
      if (n.kind === 'part') {
        it.text.setAttribute('class', 'tpl-m3d__ov-label tpl-m3d__ov-head');
        if (n.sub) {
          it.sub = svg('text', 'tpl-m3d__ov-label tpl-m3d__ov-sub', g);
          it.sub.textContent = n.sub;
        }
      }
      return it;
    });
  }

  function fx(v) { return v.toFixed(1); }

  function dotAt(it, x, y) {
    it.dot.setAttribute('cx', fx(x)); it.dot.setAttribute('cy', fx(y)); it.dot.setAttribute('r', '2.5');
    if (it.ring) { it.ring.setAttribute('cx', fx(x)); it.ring.setAttribute('cy', fx(y)); it.ring.setAttribute('r', '4.5'); }
  }

  NS.paintOverlay = function (el, p, project, wide) {
    if (!el.ov || !el.items) return;
    var b = el.box;
    if (!b) return;
    var placed = [];
    el.items.forEach(function (it) {
      var n = it.n;
      var alpha = ease(seg(p, n.p0 - 0.012, n.p0 + 0.012)) * (1 - ease(seg(p, n.p1 - 0.012, n.p1 + 0.012)));
      if (n.kind === 'part' && !wide) alpha = 0;
      var A = alpha > 0.01 ? project(n.plane, n.a[0], n.a[1]) : null;
      if (!A) { if (it.on !== 0) { it.g.style.opacity = '0'; it.on = 0; } return; }
      var ax = b.x + A[0] * b.k, ay = b.y + A[1] * b.k;
      if (n.kind === 'dim') {
        var B = project(n.plane, n.b[0], n.b[1]);
        if (!B) { it.g.style.opacity = '0'; it.on = 0; return; }
        var bx = b.x + B[0] * b.k, by = b.y + B[1] * b.k;
        /* extension lines run from the object to just past the dimension */
        var e0 = null, e1 = null, f0 = null, f1 = null;
        if (!n.ext) { /* an inside dimension runs edge to edge */ }
        else if (n.vertical) {
          e0 = project(n.plane, n.ext[0], n.a[1]); e1 = project(n.plane, n.ext[1], n.a[1]);
          f0 = project(n.plane, n.ext[0], n.b[1]); f1 = project(n.plane, n.ext[1], n.b[1]);
        } else {
          e0 = project(n.plane, n.a[0], n.ext[0]); e1 = project(n.plane, n.a[0], n.ext[1]);
          f0 = project(n.plane, n.b[0], n.ext[0]); f1 = project(n.plane, n.b[0], n.ext[1]);
        }
        var d = '';
        [[e0, e1], [f0, f1]].forEach(function (pair) {
          if (pair[0] && pair[1]) d += 'M' + fx(b.x + pair[0][0] * b.k) + ' ' + fx(b.y + pair[0][1] * b.k) + 'L' + fx(b.x + pair[1][0] * b.k) + ' ' + fx(b.y + pair[1][1] * b.k);
        });
        it.ext.setAttribute('d', d);
        /* the dimension itself, with short end bars */
        var dx = bx - ax, dy = by - ay, len = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = -dy / len * 4, ny = dx / len * 4;
        it.line.setAttribute('d', 'M' + fx(ax) + ' ' + fx(ay) + 'L' + fx(bx) + ' ' + fx(by) +
          'M' + fx(ax - nx) + ' ' + fx(ay - ny) + 'L' + fx(ax + nx) + ' ' + fx(ay + ny) +
          'M' + fx(bx - nx) + ' ' + fx(by - ny) + 'L' + fx(bx + nx) + ' ' + fx(by + ny));
        var mx = (ax + bx) / 2, my = (ay + by) / 2;
        var ang = Math.atan2(dy, dx) * 180 / Math.PI;
        if (ang > 90) ang -= 180;
        if (ang < -90) ang += 180;
        var off = n.vertical ? -9 : -8;
        /* a vertical label reads up the line, on the side the dimension
           says (inside the window for the window's own height) */
        var side = n.side || -1;
        it.text.setAttribute('transform', 'translate(' + fx(mx + (n.vertical ? side * nx * 2.6 : 0)) + ' ' + fx(my + (n.vertical ? side * ny * 2.6 : off)) + ') rotate(' + fx(ang) + ')');
        it.text.setAttribute('text-anchor', 'middle');
        it.text.setAttribute('dominant-baseline', n.vertical ? 'middle' : 'auto');
      } else if (n.kind === 'note') {
        var lx = ax + n.dx, ly = ay + n.dy;
        it.line.setAttribute('d', 'M' + fx(ax) + ' ' + fx(ay) + 'L' + fx(lx + 6) + ' ' + fx(ly) + 'L' + fx(lx) + ' ' + fx(ly));
        dotAt(it, ax, ay);
        it.text.setAttribute('transform', 'translate(' + fx(lx - 6) + ' ' + fx(ly + 4) + ')');
        it.text.setAttribute('text-anchor', 'end');
      } else {
        /* part labels are laid out together below, in two columns */
        placed.push({ it: it, ax: ax, ay: ay, alpha: alpha });
        return;
      }
      it.g.style.opacity = alpha.toFixed(3);
      it.on = 1;
    });
    if (placed.length) layoutParts(placed, objectBounds(project, b));
  };

  /* the whole object's extent on screen, from the corners of every plane */
  function objectBounds(project, b) {
    var P = C.P, H2 = 2 * C.HO, out = { l: Infinity, r: -Infinity, t: Infinity, b: -Infinity };
    [['frame', H2], ['panel', P], ['board', P], ['spacer', P]].forEach(function (pl) {
      [[0, 0], [pl[1], 0], [0, pl[1]], [pl[1], pl[1]]].forEach(function (c) {
        var q = project(pl[0], c[0], c[1]);
        if (!q) return;
        var x = b.x + q[0] * b.k, y = b.y + q[1] * b.k;
        out.l = Math.min(out.l, x); out.r = Math.max(out.r, x); out.t = Math.min(out.t, y); out.b = Math.max(out.b, y);
      });
    });
    return out;
  }

  /* Exploded-view callouts in two columns, clear of the object: the front
     parts on the left, the back parts on the right. Each label sits level
     with its own point, so its leader is one short straight line out to the
     side; rows that would touch are pushed apart and the leader takes an
     elbow. Leaders on one side never cross. */
  var ROW = 46, GAP = 40;
  function layoutParts(list, bb) {
    [-1, 1].forEach(function (side) {
      var col = list.filter(function (q) { return (q.it.n.side || 1) === side; });
      col.sort(function (a, b) { return a.ay - b.ay; });
      var last = -Infinity;
      col.forEach(function (q) { q.ly = Math.max(q.ay, last + ROW); last = q.ly; });
      var x = side < 0 ? bb.l - GAP : bb.r + GAP;
      col.forEach(function (q) {
        var it = q.it, kink = x - side * 16;
        var d = 'M' + fx(q.ax) + ' ' + fx(q.ay);
        d += Math.abs(q.ly - q.ay) < 0.5 ? 'L' + fx(x) + ' ' + fx(q.ly) : 'L' + fx(kink) + ' ' + fx(q.ay) + 'L' + fx(x) + ' ' + fx(q.ly);
        it.line.setAttribute('d', d);
        dotAt(it, q.ax, q.ay);
        var tx = x + side * 10, anchor = side < 0 ? 'end' : 'start';
        it.text.setAttribute('transform', 'translate(' + fx(tx) + ' ' + fx(q.ly + (it.sub ? -3 : 4)) + ')');
        it.text.setAttribute('text-anchor', anchor);
        if (it.sub) {
          it.sub.setAttribute('transform', 'translate(' + fx(tx) + ' ' + fx(q.ly + 14) + ')');
          it.sub.setAttribute('text-anchor', anchor);
        }
        it.g.style.opacity = q.alpha.toFixed(3);
        it.on = 1;
      });
    });
  }

  /* the media box in sticky pixels, measured only when it may have moved */
  NS.measure = function (el, media, mediaPx) {
    var s = el.sticky.getBoundingClientRect();
    var m = media.getBoundingClientRect();
    el.box = { x: m.left - s.left, y: m.top - s.top, k: m.width / mediaPx };
    if (el.ov) el.ov.setAttribute('viewBox', '0 0 ' + Math.round(s.width) + ' ' + Math.round(s.height));
  };

  NS.initOverlay = function (el, win) {
    if (el.ov) buildOverlay(el, NS.annotations(win, el.parts));
  };

  /* ---- the last stage: the wall, a paperback for scale, the museum
     label, the choices and the next step ---- */
  function optSelect(name) { return document.querySelector('select[name="options[' + name + ']"]'); }
  function sizeRadio(v) {
    return document.querySelector('input[type="radio"][name="options[Size]"]' + (v ? '[value="' + v + '"]' : ':checked'));
  }
  function addButton() {
    return document.querySelector('form[id^="product-form-"][action*="/cart/add"] [name="add"]') ||
      document.querySelector('form[action*="/cart/add"] [name="add"]');
  }
  NS.endState = function (stage, el) {
    var ms = optSelect('Mat'), fs = optSelect('Framing') || optSelect('Frame'), sr = sizeRadio(), h = stage.tplHint || {};
    return {
      mat: (ms && ms.value) || h.mat || stage.getAttribute('data-s3d-mat') || 'Marine',
      finish: (fs && fs.value) || h.finish || stage.getAttribute('data-s3d-finish') || 'Ebony Black',
      size: (sr && sr.value) || el.sizeLocal || '10 inch'
    };
  };

  NS.initEnd = function (stage, el) {
    if (!el.end || el.endWired) return;
    el.endWired = true;
    /* the next step replaces the old closing button */
    el.ctaOff = true;
    if (el.cta) el.cta.hidden = true;
    var arBtn = el.end.querySelector('[data-s3d-ar]'), addBtn = el.end.querySelector('[data-s3d-add]');
    if (arBtn && document.querySelector('[data-ar-trigger]')) {
      arBtn.hidden = false;
      arBtn.addEventListener('click', function () { var t = document.querySelector('[data-ar-trigger]'); if (t) t.click(); });
    }
    if (addBtn && addButton()) {
      addBtn.hidden = false;
      addBtn.addEventListener('click', function () { var b = addButton(); if (b && !b.disabled) b.click(); });
      /* The store disables its button while a new variant loads, then re-enables it (or swaps it
         for a fresh one). Copy its state whenever it changes, not only when a choice is made,
         or the 3D button stays greyed after every change. */
      var mirrorAdd = function () { var b = addButton(); if (b) addBtn.disabled = !!b.disabled; };
      new MutationObserver(mirrorAdd).observe(document.querySelector('product-info') || document.body,
        { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
      mirrorAdd();
    }
    function sync() {
      var st = NS.endState(stage, el);
      el.size = st.size;
      el.end.querySelectorAll('[data-s3d-pick]').forEach(function (b) {
        var cur = b.getAttribute('data-s3d-pick') === 'Mat' ? st.mat : st.finish;
        b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === cur));
      });
      el.end.querySelectorAll('[data-s3d-size]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-s3d-size') === st.size));
      });
      var big = /16/.test(st.size);
      if (el.sizeNote) el.sizeNote.textContent = 'Framed, about ' + (big ? '17\u00bc' : '11\u00bc') + ' in.' + (el.book ? ' Shown beside a paperback.' : '');
      if (el.spec) el.spec.textContent = st.mat + ' \u00b7 ' + st.finish + ' \u00b7 ' + st.size.replace(/\s*inch/, ' in');
      var b = addButton();
      if (addBtn && b) addBtn.disabled = !!b.disabled;
    }
    el.endSync = sync;
    function choose(option, value) {
      var sel = optSelect(option) || (option === 'Framing' ? optSelect('Frame') : null);
      if (sel) {
        if (sel.value !== value) { sel.value = value; sel.dispatchEvent(new Event('change', { bubbles: true })); }
      } else {
        stage.tplHint = Object.assign({}, stage.tplHint || {}, option === 'Mat' ? { mat: value } : { finish: value });
        stage.dispatchEvent(new CustomEvent('tpl-s3d-look'));
      }
      sync();
    }
    el.end.addEventListener('click', function (event) {
      var pick = event.target.closest('[data-s3d-pick]');
      if (pick) { choose(pick.getAttribute('data-s3d-pick'), pick.getAttribute('data-value')); return; }
      var sz = event.target.closest('[data-s3d-size]');
      if (!sz) return;
      var v = sz.getAttribute('data-s3d-size'), r = sizeRadio(v);
      if (r) { if (!r.checked) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); } }
      else el.sizeLocal = v;
      stage.classList.add('is-resizing');
      clearTimeout(el.resizeTimer);
      el.resizeTimer = setTimeout(function () { stage.classList.remove('is-resizing'); }, 650);
      sync();
      if (stage.tplRepaint) stage.tplRepaint();
    });
    /* follow the buy box both ways */
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!t || !t.name || !/^options\[/.test(t.name)) return;
      requestAnimationFrame(function () { sync(); if (stage.tplRepaint) stage.tplRepaint(); });
    });
    sync();
  };

  /* Per frame: the stage class, the wall's strength, and the paperback
     standing on the floor beside the frame at true relative scale (it is
     198 mm tall; the frame is 11 1/4 or 17 1/4 in). */
  NS.paintEnd = function (el, p, project) {
    if (!el.end) return;
    var on = p > 0.9;
    if (on !== el.endOn) { el.endOn = on; el.stage.classList.toggle('is-end', on); }
    var wall = ease(seg(p, 0.86, 0.94));
    if (wall !== el.wallLevel) { el.wallLevel = wall; el.stage.style.setProperty('--m3d-wall', wall.toFixed(3)); }
    var book = el.book, b = el.box;
    if (!book || !b) return;
    var a = ease(seg(p, 0.88, 0.95));
    var A = null, B = null, T = null, H2 = 2 * C.HO;
    var bh = 198 * H2 / (/16/.test(el.size || '') ? 438 : 286), bw = bh * 129 / 198, u0 = H2 + 34;
    if (a > 0.01) { A = project('frame', u0, 0); B = project('frame', u0 + bw, 0); T = project('frame', u0, bh); }
    if (!A || !B || !T) { if (book.style.opacity !== '0') book.style.opacity = '0'; return; }
    var x = b.x + A[0] * b.k, y = b.y + A[1] * b.k, w = (B[0] - A[0]) * b.k, h = (A[1] - T[1]) * b.k;
    book.style.cssText = 'opacity:' + a.toFixed(3) + ';left:' + x.toFixed(1) + 'px;top:' + (y - h).toFixed(1) + 'px;width:' + w.toFixed(1) + 'px;height:' + h.toFixed(1) + 'px';
  };

  /* ---- "Make it yours" at the end stage (owner 2026-10-30) ----
     A cobalt pill under the last caption. On a product page it presses the
     main Add to cart, so the form submits and tpl-cart-event-bridge.js and
     the drawer take it from there; it never posts to the cart itself. Label,
     price and sold-out follow the selected variant. With no product it is a
     link to the product grid. It fades in only at the end of the scroll. */
  NS.BUY_LEAD = 0.12;
  NS.paintBuy = function (el, p) {
    if (!el.buy || el.buyOff || !el.buyWired) return;
    var on = p >= NS.endP(el.stage) - NS.BUY_LEAD;
    if (on !== el.buyOn) { el.buyOn = on; el.stage.classList.toggle('is-buy', on); }
  };

  function bucks() {
    try { if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') window.bucksCC.reConvert(); } catch (e) {}
  }

  NS.initBuy = function (stage, el) {
    var box = el.buy;
    if (!box || el.buyWired) return;
    el.buyWired = true;
    function off() { el.buyOff = true; box.hidden = true; stage.classList.remove('is-buy'); }
    /* run on to the finished piece and the closing card has its own Add */
    if (el.end && NS.endP(stage) === 1) { off(); return; }
    /* the old closing button would say the same thing twice */
    el.ctaOff = true;
    if (el.cta) el.cta.hidden = true;
    function track(mode, id) {
      try { if (window.tplTrack) window.tplTrack('tpl_s3d_atc', { mode: mode, variant_id: id || '' }); } catch (e) {}
    }

    var link = box.querySelector('[data-s3d-buy-link]');
    if (link) {
      var target = document.getElementById((link.getAttribute('href') || '').replace(/^#/, ''));
      if (!target) { off(); return; }
      link.addEventListener('click', function (event) {
        event.preventDefault();
        track('choose');
        var calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (window.tplLenis && !calm) window.tplLenis.scrollTo(target, { offset: -64 });
        else target.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
      });
      return;
    }

    var btn = box.querySelector('[data-s3d-buy-btn]');
    if (!btn || !addButton()) { off(); return; }
    var rows = {};
    try { rows = JSON.parse((box.querySelector('[data-s3d-variants]') || {}).textContent || '{}') || {}; } catch (e) { rows = {}; }
    var text = btn.getAttribute('data-s3d-buy-text') || 'Make it yours';
    var labelEl = btn.querySelector('[data-s3d-buy-label]'), cost = btn.querySelector('[data-s3d-buy-cost]');
    var lastId = '';

    function mainForm() {
      var b = addButton();
      return b ? b.form || b.closest('form') : null;
    }
    function currentId() {
      var f = mainForm(), input = f && f.querySelector('input[name="id"]');
      return input ? String(input.value) : '';
    }
    /* Bucks rewrites .money spans in place; put a fresh shop-format span in
       and let it convert that one (the pattern tpl-mat-pair-price.js uses) */
    function putPrice(money) {
      var old = btn.querySelector('[data-s3d-buy-price]');
      if (!old || !money) return;
      var span = document.createElement('span');
      span.className = 'money';
      span.setAttribute('data-s3d-buy-price', '');
      span.textContent = money;
      old.replaceWith(span);
      bucks();
    }
    function sync() {
      var main = addButton(), id = currentId(), row = rows[id];
      var sold = row ? !row[1] : !!(main && /sold out|unavailable/i.test(main.textContent || ''));
      if (row && id !== lastId) { lastId = id; putPrice(row[0]); }
      var label = sold ? 'Sold out' : text;
      if (labelEl && labelEl.textContent !== label) labelEl.textContent = label;
      if (cost) cost.hidden = sold;
      /* the store disables its button while a variant loads or a line is
         being added; this one follows it */
      btn.disabled = sold || !main || !!main.disabled;
    }
    btn.addEventListener('click', function () {
      var main = addButton();
      if (btn.disabled || !main || main.disabled) return;
      track('add', currentId());
      main.click();
    });
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.name && (t.name === 'id' || /^options\[/.test(t.name))) requestAnimationFrame(sync);
    });
    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        window.subscribe(window.PUB_SUB_EVENTS.variantChange, function () { setTimeout(sync, 0); });
      }
    } catch (e) {}
    var root = document.querySelector('product-info') || mainForm() || document.body;
    new MutationObserver(sync).observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'value'] });
    sync();
    window.addEventListener('load', bucks, { once: true });
  };

  /* "Make it yours" takes you to the buy block with the mat on screen
     selected. It never adds to the cart. A section link is left alone. */
  NS.wireCta = function (stage, el, currentMat) {
    var btn = stage.querySelector('[data-s3d-cta-btn]');
    if (!btn) return;
    var form = document.querySelector('.product-form__buttons, [name="add"]');
    var target = form || document.querySelector('.product__info-wrapper');
    if (!target) { el.ctaOff = true; if (el.cta) el.cta.hidden = true; return; }
    btn.addEventListener('click', function () {
      var mat = currentMat();
      if (mat) {
        var tile = document.querySelector('.tpl-mat-swatch[data-mat-value="' + mat + '"]');
        if (tile && tile.getAttribute('aria-checked') !== 'true') tile.click();
      }
      if (window.tplLenis) window.tplLenis.scrollTo(target, { offset: -120 });
      else target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  /* One registration point. The storefront always has the bus; the bare
     fallback exists only so a missing bus degrades instead of freezing. */
  NS.addPainter = function (painter) {
    if (window.TPLScrollBus && window.TPLScrollBus.add) { window.TPLScrollBus.add(painter); return; }
    window.addEventListener('scroll', function () { painter.write(painter.read()); }, { passive: true });
  };
  NS.removePainter = function (painter) {
    if (window.TPLScrollBus && window.TPLScrollBus.remove) window.TPLScrollBus.remove(painter);
  };

  NS.progressOf = function (stage, sticky) {
    var r = stage.getBoundingClientRect();
    var sh = sticky ? sticky.offsetHeight : window.innerHeight;
    var top = sticky ? parseFloat(getComputedStyle(sticky).top) || 0 : 0;
    var total = r.height - sh;
    return total > 0 ? clamp((top - r.top) / total, 0, 1) * NS.endP(stage) : 0;
  };

  NS.near = function (stage) {
    var r = stage.getBoundingClientRect();
    return r.top < window.innerHeight * 2.5 && r.bottom > -window.innerHeight;
  };

  /* ---- on-demand scripts ---- */
  var loading = {};
  NS.loadScript = function (url) {
    if (!url) return Promise.reject(new Error('no url'));
    if (loading[url]) return loading[url];
    loading[url] = new Promise(function (resolve, reject) {
      var tag = document.createElement('script');
      tag.src = url;
      tag.async = true;
      tag.onload = resolve;
      tag.onerror = function () { reject(new Error('failed: ' + url.split('?')[0].split('/').pop())); };
      document.head.appendChild(tag);
    });
    return loading[url];
  };

  /* three.js arrives as an injected module script: Shopify's pipeline turns
     a dynamic import() inside a .js asset into require(). */
  var threePromise = null;
  NS.loadThree = function (url) {
    if (threePromise) return threePromise;
    threePromise = new Promise(function (resolve, reject) {
      if (window.tplThree) { resolve(window.tplThree); return; }
      var timer = setTimeout(function () { reject(new Error('three.js timed out')); }, 15000);
      window.addEventListener('tpl:three-ready', function () {
        clearTimeout(timer);
        if (window.tplThree) resolve(window.tplThree);
        else reject(new Error('three.js loaded but empty'));
      }, { once: true });
      var tag = document.createElement('script');
      tag.type = 'module';
      tag.textContent = "import * as T from '" + url + "';window.tplThree = T;window.dispatchEvent(new Event('tpl:three-ready'));";
      tag.onerror = function () { clearTimeout(timer); reject(new Error('three.js failed to load')); };
      document.head.appendChild(tag);
    });
    return threePromise;
  };

  /* ---- Lenis, on pages that carry this section, never on touch ---- */
  var lenisStarted = false;
  function startLenis(url) {
    if (lenisStarted || !url) return;
    lenisStarted = true;
    var css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = url.replace(/\.js($|\?)/, '.css$1');
    var gaveUp = false;
    var bail = setTimeout(function () { gaveUp = true; }, 4000);
    var tag = document.createElement('script');
    tag.src = url;
    tag.onerror = function () { clearTimeout(bail); gaveUp = true; };
    tag.onload = function () {
      clearTimeout(bail);
      if (gaveUp || !window.Lenis) return;
      var lenis = new window.Lenis({ lerp: 0.085, wheelMultiplier: 1.0, smoothWheel: true });
      window.tplLenis = lenis;
      function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
      requestAnimationFrame(raf);
      lenis.on('scroll', function () {
        if (window.TPLScrollBus && window.TPLScrollBus.schedule) window.TPLScrollBus.schedule();
        else window.dispatchEvent(new Event('scroll'));
      });
    };
    document.head.appendChild(css);
    document.head.appendChild(tag);
  }

  function startFlat(stage, still) {
    var urls = [stage.getAttribute('data-s3d-data-js'), stage.getAttribute('data-s3d-flat-js')];
    return NS.loadScript(urls[0])
      .then(function () { return NS.loadScript(urls[1]); })
      .then(function () { return NS.flat && NS.flat.init(stage, still); })
      .catch(function (err) { stage.setAttribute('data-s3d-error', 'flat: ' + String(err && err.message || err).slice(0, 160)); return false; });
  }

  function startLive(stage) {
    return NS.loadScript(stage.getAttribute('data-s3d-scene-js'))
      .then(function () { return BAKING ? NS.loadScript(stage.getAttribute('data-s3d-bake-js')) : null; })
      .then(function () { return NS.loadThree(stage.getAttribute('data-s3d-three')); })
      .then(function (THREE) { return BAKING ? NS.bake.init(THREE, stage) : NS.scene.run(THREE, stage); })
      .catch(function (err) {
        /* never silent; on a product page the tap-to-explode block remains */
        stage.setAttribute('data-s3d-error', String((err && err.message) || err).slice(0, 180));
        if (!BAKING) startFlat(stage, false);
      });
  }

  function init(stage) {
    if (stage.tplS3dInit) return;
    stage.tplS3dInit = true;
    /* Dawn's body overflow-x: hidden kills position: sticky; tpl-mat-pdp.css
       swaps it for clip on pages carrying this class */
    document.documentElement.classList.add('tpl-s3d-page');

    var forced = (QS.match(/[?&]s3d=(frames|webgl|bake)/) || [])[1];
    /* 2026-11-01: phones with a recording (data-s3d-mvideo) play the video instead; load nothing */
    if (!forced && stage.hasAttribute('data-s3d-mvideo') && window.matchMedia('(max-width: 749px)').matches) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce && !forced) {
      if (stage.getAttribute('data-s3d-rm') === 'static') startFlat(stage, true);
      return;
    }
    var phone = window.matchMedia('(pointer: coarse)').matches;
    var mode = stage.getAttribute('data-s3d-mobile') || 'frames';
    if (stage.getAttribute('data-s3d-lenis') === 'true' && !phone && !forced) startLenis(stage.getAttribute('data-s3d-lenis-url'));

    if (forced === 'frames') { startFlat(stage, false); return; }
    if (forced) phone = false;
    if (phone && mode === 'off') return;
    if ((phone && mode === 'frames') || !window.WebGLRenderingContext) { startFlat(stage, false); return; }

    /* fetch the scene and three.js only once the stage is nearly in view;
       the rect check rides the bus read already paid for */
    var started = false;
    var near = {
      read: function () { return NS.near(stage); },
      write: function (close) {
        if (!close || started) return;
        started = true;
        /* this runs inside the bus's write pass; leave its list alone */
        setTimeout(function () { NS.removePainter(near); }, 0);
        startLive(stage);
      }
    };
    NS.addPainter(near);
    if (BAKING) near.write(true);
  }

  function boot(root) {
    (root || document).querySelectorAll('[data-tpl-mat-scroll3d]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    var t = event.target;
    if (!t || !t.querySelectorAll) return;
    t.querySelectorAll('[data-tpl-mat-scroll3d]').forEach(function (s) { if (s.tplDispose) s.tplDispose(); });
  });
})();
